import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const ShareModal = ({ isOpen, onClose, collection, onSave }) => {
  const [isPublic, setIsPublic] = useState(collection?.is_public || false);
  const [showcaseSettings, setShowcaseSettings] = useState(
    collection?.showcase_settings || {
      show_values: true,
      show_descriptions: true,
      featured_items_count: 6,
      show_owner_info: true,
      allow_comments: false
    }
  );
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const publicUrl = `${window?.location?.origin}/public-collection?id=${collection?.id}`;

  const handleSave = async () => {
    setSaving(true);
    await onSave(isPublic, showcaseSettings);
    setSaving(false);
  };

  const handleCopyLink = () => {
    navigator?.clipboard?.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const updateSetting = (key, value) => {
    setShowcaseSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Share2" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-card-foreground">Share Collection</h2>
              <p className="text-sm text-muted-foreground">Customize how your collection appears publicly</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Public Toggle */}
          <div className="flex items-start space-x-3 p-4 bg-muted/20 rounded-lg">
            <Checkbox
              checked={isPublic}
              onChange={(e) => setIsPublic(e?.target?.checked)}
              id="public-toggle"
            />
            <div className="flex-1">
              <label htmlFor="public-toggle" className="font-semibold text-card-foreground cursor-pointer">
                Make Collection Public
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Allow anyone with the link to view your collection
              </p>
            </div>
          </div>

          {/* Public Link */}
          {isPublic && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">Public Link</label>
              <div className="flex items-center space-x-2">
                <Input
                  value={publicUrl}
                  readOnly
                  className="flex-1 bg-muted/50"
                />
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  iconName={copied ? 'Check' : 'Copy'}
                  iconPosition="left"
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          )}

          {/* Showcase Settings */}
          {isPublic && (
            <div className="space-y-4">
              <h3 className="font-semibold text-card-foreground">Showcase Settings</h3>
              
              {/* Show Values */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={showcaseSettings?.show_values}
                  onChange={(e) => updateSetting('show_values', e?.target?.checked)}
                  id="show-values"
                />
                <div className="flex-1">
                  <label htmlFor="show-values" className="text-sm font-medium text-card-foreground cursor-pointer">
                    Show Item Values
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Display purchase price and current value for each item
                  </p>
                </div>
              </div>

              {/* Show Descriptions */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={showcaseSettings?.show_descriptions}
                  onChange={(e) => updateSetting('show_descriptions', e?.target?.checked)}
                  id="show-descriptions"
                />
                <div className="flex-1">
                  <label htmlFor="show-descriptions" className="text-sm font-medium text-card-foreground cursor-pointer">
                    Show Item Descriptions
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Include detailed descriptions and notes for each item
                  </p>
                </div>
              </div>

              {/* Show Owner Info */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={showcaseSettings?.show_owner_info}
                  onChange={(e) => updateSetting('show_owner_info', e?.target?.checked)}
                  id="show-owner"
                />
                <div className="flex-1">
                  <label htmlFor="show-owner" className="text-sm font-medium text-card-foreground cursor-pointer">
                    Show Owner Information
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Display your name and profile information
                  </p>
                </div>
              </div>

              {/* Featured Items Count */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">
                  Featured Items Count
                </label>
                <Input
                  type="number"
                  min="3"
                  max="12"
                  value={showcaseSettings?.featured_items_count}
                  onChange={(e) => updateSetting('featured_items_count', parseInt(e?.target?.value) || 6)}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Number of items to highlight (3-12)
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            iconName={saving ? 'Loader2' : 'Save'}
            iconPosition="left"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;